import { Injectable, signal } from '@angular/core';

/**
 * Shares the user's currently-focused ticker across decision-flow sections
 * (NAV_RESTRUCTURE_SPEC Change 6). When the user analyzes TSLA in Options
 * Analysis, the Research / Sentiment / Earnings pages pre-populate to TSLA
 * on next load so they don't have to retype the symbol at every step.
 *
 * Stored in sessionStorage so it persists across navigations but resets
 * when the user closes the tab — that's a sensible default for "what am I
 * looking at right now."
 */
@Injectable({ providedIn: 'root' })
export class TickerContextService {
  private static readonly STORAGE_KEY = 'hub_active_ticker';

  // Signal first; the storage read is the fallback for fresh tabs.
  activeTicker = signal<string | null>(
    typeof sessionStorage === 'undefined'
      ? null
      : sessionStorage.getItem(TickerContextService.STORAGE_KEY)
  );

  set(ticker: string | null | undefined): void {
    if (!ticker) {
      this.clear();
      return;
    }
    const upper = String(ticker).trim().toUpperCase();
    if (!upper) {
      this.clear();
      return;
    }
    this.activeTicker.set(upper);
    try {
      sessionStorage.setItem(TickerContextService.STORAGE_KEY, upper);
    } catch {
      // Private-browsing / quota errors — fine, signal still works for this tab.
    }
  }

  get(): string | null {
    const fromSignal = this.activeTicker();
    if (fromSignal) return fromSignal;
    try {
      return sessionStorage.getItem(TickerContextService.STORAGE_KEY);
    } catch {
      return null;
    }
  }

  clear(): void {
    this.activeTicker.set(null);
    try {
      sessionStorage.removeItem(TickerContextService.STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}
